import { DropdownMenu } from '@kobalte/core/dropdown-menu'
import { createUniqueId, For, Show, type JSX } from 'solid-js'

import { Icon } from '../icons'
import Css from './ui.module.scss'

export type MenuOption = {
    label: string
    current: boolean
    onSelect: () => Promise<void>
    /** Opcao que nao se muda - no seletor de colunas, o indicador em foco. */
    disabled?: boolean
}

/**
 * O seletor de um punhado de opcoes - periodo, metrica, e o que mais escolher
 * entre poucos valores conhecidos.
 *
 * Sobre o `DropdownMenu` do Kobalte, e nao um `<div>` com um sinal: teclado, foco,
 * Escape, clique fora e `aria-expanded` vem prontos e corretos. Escrever isso a mao
 * de novo em cada tela e como se perde acessibilidade sem perceber.
 *
 * Qual opcao esta em vigor e do SCOPE (`current`), nao daqui: e parametro de place,
 * sobrevive ao F5 e viaja no link. O que fica no componente e so se a lista esta
 * aberta.
 */
export function Menu(props: {
    summary: string
    options: MenuOption[]
    icon?: string
    /** Sobre fundo escuro - o seletor dentro do banner. */
    onDark?: boolean
    /**
     * Desliga o gatilho, mantendo o campo na tela.
     *
     * Para quando ainda nao ha o que escolher: o campo continua ocupando o lugar
     * dele e dizendo do que se trata, em vez de sumir e fazer a linha inteira se
     * rearranjar quando a primeira opcao aparecer.
     */
    disabled?: boolean

    /**
     * O nome do que se escolhe aqui, desenhado em cima do gatilho.
     *
     * Opcional porque na maioria dos lugares o menu esta ao lado de um titulo que ja
     * o nomeia. Onde ele entra numa fileira de filtros, cada um com o seu rotulo, o
     * menu sem nome e o unico controle anonimo da linha - foi o que aconteceu com o
     * seletor de periodo da lista de Colaboradores, que mostrava duas datas e nao
     * dizia que aquilo era o recorte de ATIVIDADE.
     */
    label?: string

    /**
     * O resumo nao e um valor ESCOLHIDO, e sim a ausencia de escolha.
     *
     * Desenha o texto apagado, como o `ChoiceFilter` faz com o placeholder dele: numa
     * fileira de filtros, apagado quer dizer "nao esta filtrando". Um combo sempre
     * mostra algum texto, e sem isto "Todo o periodo" tinha o mesmo peso de "Últimos 30
     * dias" - dois estados opostos com a mesma cara.
     */
    empty?: boolean
    class?: string
    /**
     * Marcar VARIAS: a lista fica aberta a cada escolha, e cada opcao e um "liga e
     * desliga" (o seletor de colunas, B-42). Sem isto, escolher uma fecha a lista.
     */
    multiple?: boolean
}): JSX.Element {
    const labelId = createUniqueId()
    const summaryId = createUniqueId()

    const trigger = (
        <DropdownMenu gutter={6}>
            <DropdownMenu.Trigger
                disabled={props.disabled}
                /*
                    Os DOIS pedacos, nesta ordem: "Registrou em" e depois "Últimos
                    15 dias". So o rotulo tiraria do nome justamente o valor escolhido,
                    e so o valor deixaria quem usa leitor de tela sem saber de que
                    filtro se trata - que e o problema que o rotulo veio resolver.
                */
                aria-labelledby={props.label ? `${labelId} ${summaryId}` : undefined}
                class={`${Css.menu} ${props.class ?? ''}`}
                classList={{ [Css.onDark]: props.onDark }}
            >
                <Show when={props.icon}>
                    <Icon name={props.icon} size={16} />
                </Show>
                <span id={summaryId} class={Css.menuSummary} classList={{ [Css.empty]: props.empty }}>
                    {props.summary}
                </span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class={Css.menuArrow}>
                    <path d="m6 9 6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content class={Css.menuList}>
                    <For each={props.options}>
                        {(option) => (
                            <Show
                                when={props.multiple}
                                fallback={
                                    <DropdownMenu.Item
                                        onSelect={() => void option.onSelect()}
                                        class={Css.menuOption}
                                        classList={{ [Css.current]: option.current }}
                                    >
                                        <span class={Css.menuText}>{option.label}</span>
                                        <Show when={option.current}>
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2.5"
                                                class={Css.menuCheck}
                                            >
                                                <path
                                                    d="m5 12 5 5 9-9"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </Show>
                                    </DropdownMenu.Item>
                                }
                            >
                                <DropdownMenu.CheckboxItem
                                    checked={option.current}
                                    disabled={option.disabled}
                                    closeOnSelect={false}
                                    onChange={() => void option.onSelect()}
                                    class={Css.menuOption}
                                    classList={{ [Css.current]: option.current }}
                                >
                                    <span class={Css.menuText}>{option.label}</span>
                                    <Show when={option.current}>
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2.5"
                                            class={Css.menuCheck}
                                        >
                                            <path d="m5 12 5 5 9-9" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </Show>
                                </DropdownMenu.CheckboxItem>
                            </Show>
                        )}
                    </For>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu>
    )

    /*
        Um `<span>` e nao um `<label>`: o gatilho e um botao, e `<label for>` nao se
        liga a botao. Quem nomeia o controle para o leitor de tela e o
        `aria-labelledby` abaixo, que e o que de fato funciona aqui.
    */
    return (
        <Show when={props.label} fallback={trigger}>
            <div class={Css.labelledField}>
                <span id={labelId} class={Css.fieldLabel} classList={{ [Css.onDark]: props.onDark }}>
                    {props.label}
                </span>
                {trigger}
            </div>
        </Show>
    )
}
